/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";
import fetchData from "../../functions/fetchData";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { getHeaders } from "../../utility/getHeader";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import {
    Card,
    Row,
    Col,
    Button,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Checkbox,
    Table,
    Tag,
    Space,
    Typography,
    Modal,
    Tooltip,
    Statistic,
} from "antd";
import {
    PlusOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    UserOutlined,
    DollarOutlined,
    CreditCardOutlined,
    CalendarOutlined,
    SearchOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import "./paymentVouchers.css";

const { Text } = Typography;
const { Option } = Select;

export default function PaymentVouchers() {
    const [suppliers, setsuppliers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState("");
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [bankAccounts, setBankAccounts] = useState([]);
    const [bankAccountId, setBankAccountId] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [remarks, setRemarks] = useState("");
    const [discountAmount, setDiscountAmount] = useState("");
    const [isDiscountGiven, setIsDiscountGiven] = useState(false);
    const [balanceAfterPayment, setBalanceAfterPayment] = useState(0);
    const [data, setData] = useState([]);
    const [companyInfo, setCompanyInfo] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterMode, setFilterMode] = useState("All");
    const [voucherDate, setVoucherDate] = useState(dayjs());
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Payment modes with bank account requirement
    const paymentModes = [
        { value: "Cash", label: "💵 Cash / เงินสด", requiresBank: false },
        { value: "Bank", label: "🏦 Bank Transfer / โอนเงิน", requiresBank: true },
        { value: "Credit_Card", label: "💳 Credit Card / บัตรเครดิต", requiresBank: true },
        { value: "UPI", label: "📱 UPI / ยูพีไอ", requiresBank: true },
        { value: "Cheque", label: "📝 Cheque / เช็ค", requiresBank: true },
        { value: "Online", label: "💻 Online Payment / จ่ายออนไลน์", requiresBank: true }
    ];

    // Fetch suppliers, bank accounts and company info when component loads
    useEffect(() => {
        fetchData("suppliers", setsuppliers, "id", {});
        fetchBankAccounts();
        fetchCompanyInfo();
    }, []);

    const fetchBankAccounts = async () => {
        try {
            const res = await axios.get(`/bank-accounts`, getHeaders());
            if (res.data.success) {
                setBankAccounts(res.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchCompanyInfo = async () => {
        try {
            const companyData = await fetchData("company_profile", null, "id", {});
            if (companyData && companyData.length > 0) {
                setCompanyInfo(companyData[0]);
            }
        } catch (error) {
            console.error("Error fetching company info:", error);
        }
    };

    const getOutStandingBalance = async (supplierId) => {
        setSelectedSupplier(supplierId);

        if (!supplierId) {
            setOutstandingAmount(0);
            return;
        }

        try {
            const res = await axios.get(`/supplier-ledger-balance/${supplierId}`, getHeaders());
            if (res.data.success) {
                const balance = res.data.data.balance || 0;
                setOutstandingAmount(Math.abs(balance));
            } else {
                toast.error("Failed to fetch supplier outstanding balance");
            }
        } catch (error) {
            console.error("Error fetching outstanding balance:", error);
            toast.error("Error fetching outstanding balance.");
        }
    };

    useEffect(() => {
        // A discount received from the supplier also clears outstanding balance
        // without any cash/bank outflow, so it's added to the payment for this calc.
        const payment = parseFloat(paymentAmount) || 0;
        const discount = isDiscountGiven ? (parseFloat(discountAmount) || 0) : 0;
        const remaining = outstandingAmount - (payment + discount);
        setBalanceAfterPayment(remaining >= 0 ? remaining : 0);
    }, [paymentAmount, outstandingAmount, discountAmount, isDiscountGiven]);

    const fetchVoucherData = async () => {
        try {
            setLoading(true);
            const vouchers = await fetchData("payment_vouchers", null, "id", {});
            setData(vouchers || []);
        } catch (error) {
            console.error("Error fetching payment vouchers:", error);
            toast.error("Failed to load payment vouchers");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        if (!selectedSupplier || !paymentAmount || !paymentMode) {
            toast.error("Please fill in all required fields");
            return;
        }

        // Check if bank account is required but not selected
        const selectedMode = paymentModes.find(mode => mode.value === paymentMode);
        if (selectedMode && selectedMode.requiresBank && !bankAccountId) {
            toast.error('Please select bank account for this payment mode');
            return;
        }

        const paymentData = {
            supplier_id: selectedSupplier,
            amount: parseFloat(paymentAmount) || 0,
            reference_number: referenceNumber,
            payment_mode: paymentMode,
            bank_account_id: bankAccountId || null,
            description: remarks,
            remarks: remarks,
            payment_date: voucherDate.format('YYYY-MM-DD'),
            discount_amount: isDiscountGiven ? parseFloat(discountAmount) || 0 : 0,
            is_discount_given: isDiscountGiven
        };

        setLoading(true);
        try {
            const res = await axios.post(`/payment-vouchers`, paymentData, getHeaders());

            if (res.data.success) {
                toast.success("Payment voucher created successfully!");
                await fetchVoucherData();

                // Reset form
                setSelectedSupplier("");
                setPaymentAmount("");
                setPaymentMode("");
                setBankAccountId("");
                setReferenceNumber("");
                setRemarks("");
                setDiscountAmount("");
                setIsDiscountGiven(false);
                setOutstandingAmount(0);
                setBalanceAfterPayment(0);
                setVoucherDate(dayjs());
                setShowCreateModal(false);
            }
        } catch (error) {
            console.error("Error saving payment:", error);
            toast.error(`Error creating payment voucher: ${error?.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePrintPaymentVoucher = async (voucherData) => {
        try {
            setLoading(true);

            // Get supplier details
            const supplier = suppliers.find(s => s.id === parseInt(voucherData.supplier_id));
            const supplierName = supplier ? supplier.supplier_name : 'N/A';
            const supplierAddress = supplier ? supplier.address || 'N/A' : 'N/A';
            const supplierPhone = supplier ? supplier.phone || 'N/A' : 'N/A';

            // Format date and time
            const paymentDate = new Date(voucherData.payment_date);
            const formattedDate = paymentDate.toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });

            // Convert amount to Thai Baht text
            const amountValue = parseFloat(voucherData.amount || 0);
            const discountAmountValue = parseFloat(voucherData.discount_amount || 0) || 0;
            const hasDiscount = voucherData.is_discount_given || discountAmountValue > 0;
            const totalAmountValue = amountValue + (hasDiscount ? discountAmountValue : 0);
            const amountInText = numberToThaiText(totalAmountValue);

            // Process logo - handle blob/base64/buffer
            let logoSrc = '';
            if (companyInfo.logo) {
                try {
                    let logoData;

                    // Check if it's a Buffer-like object (from database BLOB)
                    if (companyInfo.logo.type === 'Buffer' && Array.isArray(companyInfo.logo.data)) {
                        // Convert Buffer array to base64
                        const uint8Array = new Uint8Array(companyInfo.logo.data);
                        let binary = '';
                        for (let i = 0; i < uint8Array.byteLength; i++) {
                            binary += String.fromCharCode(uint8Array[i]);
                        }
                        logoData = btoa(binary);
                        logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                    }
                    // Check if it's already a base64 string
                    else if (typeof companyInfo.logo === 'string') {
                        logoData = companyInfo.logo;

                        if (logoData.startsWith('data:image')) {
                            // Already base64 with prefix
                            logoSrc = logoData;
                        } else if (logoData.startsWith('blob:')) {
                            // Blob URL
                            logoSrc = logoData;
                        } else {
                            // Assume it's base64 without prefix
                            logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                        }
                    }
                } catch (error) {
                    console.error('Error processing logo:', error);
                    logoSrc = ''; // Fallback to no logo
                }
            }

            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>Payment Voucher</title>
                    <style>
                        @page {
                            size: A4;
                            margin: 10mm 5mm;
                        }

                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }

                        body {
                            font-family: Arial, 'Sarabun', sans-serif;
                            font-size: 10pt;
                            line-height: 1.4;
                            color: #000;
                            background: white;
                        }

                        .voucher-container {
                            max-width: 210mm;
                            margin: 0 auto;
                            padding: 8mm;
                            border: 2px solid #333;
                            background: white;
                        }

                        .header-section {
                            display: flex;
                            align-items: flex-start;
                            margin-bottom: 15px;
                            padding-bottom: 15px;
                            border-bottom: 2px solid #ddd;
                            background: white;
                            padding: 15px;
                        }

                        .logo-section {
                            flex: 0 0 120px;
                            margin-right: 15px;
                        }

                        .logo-section img {
                            max-width: 100px;
                            max-height: 80px;
                            object-fit: contain;
                        }

                        .company-info {
                            flex: 1;
                        }

                        .company-name {
                            font-size: 14pt;
                            font-weight: bold;
                            margin-bottom: 3px;
                            color: #000;
                        }

                        .company-details {
                            font-size: 8pt;
                            line-height: 1.3;
                            color: #333;
                        }

                        .voucher-title {
                            text-align: center;
                            font-size: 16pt;
                            font-weight: bold;
                            margin: 15px 0;
                            padding: 10px;
                            background: linear-gradient(135deg, #0d9488 0%, #0d9488 100%);
                            color: white;
                            border-radius: 5px;
                            text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
                        }

                        .info-section {
                            margin-bottom: 15px;
                            padding: 10px;
                            background: #f9f9f9;
                            border-radius: 5px;
                            border-left: 4px solid #ddd;
                        }

                        .info-row {
                            display: flex;
                            margin-bottom: 5px;
                            font-size: 10pt;
                        }

                        .info-label {
                            font-weight: bold;
                            min-width: 100px;
                            color: #000;
                        }

                        .info-value {
                            flex: 1;
                        }

                        .right-align {
                            text-align: right;
                            flex: 1;
                            color: #000;
                            font-weight: bold;
                        }

                        .amount-in-words {
                            margin: 10px 0;
                            padding: 12px;
                            background: #fffef0;
                            border-left: 4px solid #ddd;
                            font-weight: bold;
                            font-size: 10pt;
                            border-radius: 5px;
                        }

                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 15px 0;
                            font-size: 9pt;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        }

                        table, th, td {
                            border: 1px solid #B0BEC5;
                        }

                        th {
                            background: #f5f5f5;
                            padding: 10px;
                            text-align: center;
                            font-weight: bold;
                            color: #000;
                        }

                        td {
                            padding: 8px;
                            background: white;
                        }

                        .text-center {
                            text-align: center;
                        }

                        .text-right {
                            text-align: right;
                        }

                        .total-row {
                            font-weight: bold;
                            background: #f0f0f0;
                            color: #000;
                        }

                        .signature-section {
                            display: flex;
                            justify-content: space-between;
                            margin-top: 40px;
                            font-size: 9pt;
                        }

                        .signature-box {
                            text-align: center;
                            flex: 0 0 30%;
                            padding: 10px;
                            background: #f9f9f9;
                            border-radius: 5px;
                            border: 1px solid #ddd;
                        }

                        .signature-line {
                            border-top: 1px solid #000;
                            margin-top: 50px;
                            padding-top: 8px;
                            font-weight: bold;
                            color: #000;
                        }

                        .footer-note {
                            margin-top: 20px;
                            font-size: 8pt;
                            color: #666;
                            text-align: center;
                            padding: 10px;
                            background: #f9f9f9;
                            border-radius: 5px;
                            border-top: 1px solid #ddd;
                        }

                        @media print {
                            body {
                                margin: 0;
                                padding: 0;
                            }

                            .voucher-container {
                                border: 2px solid #000;
                                page-break-inside: avoid;
                                box-shadow: none;
                            }

                            .header-section {
                                background: #f0f0f0 !important;
                                border-bottom: 2px solid #000 !important;
                            }

                            .voucher-title {
                                background: #e0e0e0 !important;
                                color: #000 !important;
                            }

                            .info-section {
                                background: #f5f5f5 !important;
                                border-left: 3px solid #000 !important;
                            }

                            .amount-in-words {
                                background: #fffef0 !important;
                                border-left: 3px solid #000 !important;
                            }

                            th {
                                background: #e0e0e0 !important;
                                color: #000 !important;
                            }

                            .total-row {
                                background: #f0f0f0 !important;
                            }

                            .signature-box {
                                background: #f5f5f5 !important;
                                border: 1px solid #000 !important;
                            }

                            .signature-line {
                                border-top: 1px solid #000 !important;
                                color: #000 !important;
                            }

                            .footer-note {
                                background: #f5f5f5 !important;
                                border-left: 2px solid #000 !important;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="voucher-container">
                        <!-- Header Section -->
                        <div class="header-section">
                            ${logoSrc ? `
                            <div class="logo-section">
                                <img src="${logoSrc}" alt="${companyInfo.name || 'Company Logo'}" />
                            </div>
                            ` : ''}
                            <div class="company-info">
                                <div class="company-name">${companyInfo.name || 'COMPANY NAME'}</div>
                                <div class="company-details">
                                    <strong>Reg No:</strong> ${companyInfo.tax_id || 'XXXXXXXXXXXXX'}<br>
                                    <strong>Address:</strong> ${companyInfo.address || 'Company Address'}${companyInfo.city ? `, ${companyInfo.city}` : ''}${companyInfo.state ? `, ${companyInfo.state}` : ''}${companyInfo.zip_code ? `, ${companyInfo.zip_code}` : ''}<br>
                                    <strong>Tel:</strong> ${companyInfo.phone_number || 'XXX-XXX-XXXX'}
                                    ${companyInfo.email ? ` | <strong>Email:</strong> ${companyInfo.email}` : ''}<br>
                                    ${companyInfo.website ? `<strong>Website:</strong> ${companyInfo.website}` : ''}
                                </div>
                            </div>
                        </div>

                        <!-- Title -->
                        <div class="voucher-title">PAYMENT VOUCHER</div>

                        <!-- Voucher Info -->
                        <div class="info-section">
                            <div style="display: flex; justify-content: space-between;">
                                <div style="flex: 1;">
                                    <div class="info-row">
                                        <span class="info-label">PAY TO</span>
                                        <span class="info-value">${supplierName}</span>
                                    </div>
                                    <div class="info-row">
                                        <span style="min-width: 100px;"></span>
                                        <span class="info-value">${supplierAddress}</span>
                                    </div>
                                    ${supplierPhone && supplierPhone !== 'N/A' ? `
                                    <div class="info-row">
                                        <span style="min-width: 100px;"></span>
                                        <span class="info-value">${supplierPhone}</span>
                                    </div>
                                    ` : ''}
                                </div>
                                <div style="flex: 0 0 200px; text-align: right;">
                                    <div style="margin-bottom: 5px;">
                                        <strong>Voucher No.:</strong> ${voucherData.voucher_number || `PV-${String(voucherData.id).padStart(6, '0')}`}
                                    </div>
                                    <div>
                                        <strong>Date:</strong> ${formattedDate}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Amount in Words -->
                        <div class="amount-in-words">
                            PAY THE SUM OF<br>
                            <span style="text-transform: uppercase;">${amountInText}</span>
                        </div>

                        <!-- Payment Details Table -->
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 20%;">Payment By</th>
                                    <th style="width: 25%;">Cheque No.</th>
                                    <th style="width: 55%;">Payment Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="text-center">${getPaymentModeInThai(voucherData.payment_mode)}</td>
                                    <td class="text-center">${voucherData.reference_id || '-'}</td>
                                    <td class="text-right">${(() => {
                                        const amount = parseFloat(voucherData.amount || 0);
                                        return amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    })()}</td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Details Table -->
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 15%;">Acc. No.</th>
                                    <th style="width: 65%;">Description</th>
                                    <th style="width: 20%;">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="text-center">400-S001</td>
                                    <td>Payment to ${supplierName}${voucherData.remarks ? ` - ${voucherData.remarks}` : ''}</td>
                                    <td class="text-right">${(() => {
                                        const amount = parseFloat(voucherData.amount || 0);
                                        return amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    })()}</td>
                                </tr>
                                ${hasDiscount ? `
                                <tr>
                                    <td class="text-center">-</td>
                                    <td>Discount Given</td>
                                    <td class="text-right" style="color: #e53e3e;">${discountAmountValue.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                                ` : ''}
                                <tr>
                                    <td colspan="2" class="text-right total-row">TOTAL:</td>
                                    <td class="text-right total-row">${totalAmountValue.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Footer Note -->
                        <div class="footer-note">
                            <div style="text-align: center; margin-bottom: 5px;">
                                <strong>This payment voucher was prepared by StoreHub Accounting System developed by Cloudnet Softwares</strong>
                            </div>
                            <div style="font-size: 8pt; text-align: center; color: #666;">
                                เอกสารนี้จัดทำขึ้นตามข้อกำหนดของกฎหมายภาษีอากรไทย พ.ศ. 2560<br>
                                <em>This document is prepared in accordance with Thai Revenue Code B.E. 2560 (2017) and Accounting Standards</em>
                            </div>
                        </div>

                        <!-- Signature Section -->
                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-line">
                                    APPROVED BY
                                </div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-line">
                                    RECEIVED BY
                                </div>
                            </div>
                        </div>
                    </div>
                </body>
                </html>
            `;

            // Open print window
            const printWindow = window.open("", "_blank");
            printWindow.document.write(printContent);
            printWindow.document.close();

            printWindow.onload = () => {
                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                }, 250);
            };

            setLoading(false);
        } catch (error) {
            console.error("Error printing voucher:", error);
            toast.error("Error generating payment voucher");
            setLoading(false);
        }
    };

    // Helper function to format currency
    const formatCurrency = (amount) => {
        if (!amount || isNaN(amount)) return '฿ 0.00';
        return `฿ ${parseFloat(amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Helper function to convert payment mode to Thai
    const getPaymentModeInThai = (mode) => {
        const modes = {
            'Cash': 'เงินสด / Cash',
            'Bank': 'โอนเงินผ่านธนาคาร / Bank Transfer',
            'Cheque': 'เช็ค / Cheque',
            'Online': 'ชำระออนไลน์ / Online Payment'
        };
        return modes[mode] || mode;
    };

    // Helper function to convert number to Thai text
    const numberToThaiText = (number) => {
        const digits = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
        const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];

        if (number === 0) return 'ศูนย์บาทถ้วน';

        const [intPart, decPart] = number.toFixed(2).split('.');
        let result = '';

        // Convert integer part
        const intStr = intPart.toString();
        const len = intStr.length;

        for (let i = 0; i < len; i++) {
            const digit = parseInt(intStr[i]);
            const position = len - i - 1;

            if (digit === 0) continue;

            if (position === 1 && digit === 1) {
                result += 'สิบ';
            } else if (position === 1 && digit === 2) {
                result += 'ยี่สิบ';
            } else if (position === 0 && digit === 1 && len > 1) {
                result += 'เอ็ด';
            } else {
                result += digits[digit] + positions[position];
            }
        }

        result += 'บาท';

        // Convert decimal part
        if (parseInt(decPart) === 0) {
            result += 'ถ้วน';
        } else {
            const satangStr = decPart.toString();
            let satangText = '';

            for (let i = 0; i < satangStr.length; i++) {
                const digit = parseInt(satangStr[i]);
                const position = satangStr.length - i - 1;

                if (digit === 0 && position === 1) continue;

                if (position === 1 && digit === 1) {
                    satangText += 'สิบ';
                } else if (position === 1 && digit === 2) {
                    satangText += 'ยี่สิบ';
                } else if (position === 0 && digit === 1 && satangStr.length > 1) {
                    satangText += 'เอ็ด';
                } else {
                    satangText += digits[digit];
                    if (position === 1) satangText += 'สิบ';
                }
            }

            result += satangText + 'สตางค์';
        }

        return result;
    };

    useEffect(() => {
        const fetchAndSetData = async () => {
            try {
                await fetchVoucherData();
            } catch (error) {
                console.error("Error in useEffect:", error);
            }
        };

        fetchAndSetData();
    }, []);

    // Filter data based on search and filter mode
    const filteredData = data.filter(item => {
        const matchesSearch =
            item.voucher_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.supplier_id?.toString().includes(searchTerm) ||
            item.amount?.toString().includes(searchTerm) ||
            item.reference_id?.toString().includes(searchTerm);

        const matchesFilter = filterMode === "All" || item.payment_mode === filterMode;

        return matchesSearch && matchesFilter;
    });

    // Export filtered data to Excel (.xlsx)
    const exportExcel = () => {
        if (filteredData.length === 0) {
            toast.error("No records to export");
            return;
        }
        const worksheetData = filteredData.map(item => {
            const supplier = suppliers.find(s => s.id === parseInt(item.supplier_id));
            const supplierName = supplier ? supplier.supplier_name : `Supplier #${item.supplier_id}`;
            return {
                "Voucher No": item.voucher_number || `PV-${item.id}`,
                "Supplier": supplierName,
                "Date": item.payment_date ? new Date(item.payment_date).toLocaleDateString('th-TH') : '',
                "Mode": item.payment_mode || '',
                "Amount": parseFloat(item.amount || 0) || 0,
                "Discount Amount": parseFloat(item.discount_amount || 0) || 0,
                "Ref ID": item.reference_id || '',
            };
        });
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Payment Vouchers");
        XLSX.writeFile(workbook, `payment_vouchers_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    };

    // Export filtered data to PDF
    const exportPDF = () => {
        if (filteredData.length === 0) {
            toast.error("No records to export");
            return;
        }
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(14);
        doc.text(companyInfo.name || 'Payment Vouchers', 14, 15);

        const tableColumn = ["Voucher No", "Supplier", "Date", "Mode", "Amount", "Discount Amount", "Ref ID"];
        const tableRows = filteredData.map(item => {
            const supplier = suppliers.find(s => s.id === parseInt(item.supplier_id));
            const supplierName = supplier ? supplier.supplier_name : `Supplier #${item.supplier_id}`;
            return [
                item.voucher_number || `PV-${item.id}`,
                supplierName,
                item.payment_date ? new Date(item.payment_date).toLocaleDateString('th-TH') : '',
                item.payment_mode || '',
                (parseFloat(item.amount || 0) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                (parseFloat(item.discount_amount || 0) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                item.reference_id || '-',
            ];
        });

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 22,
        });

        doc.save(`payment_vouchers_${dayjs().format('YYYY-MM-DD')}.pdf`);
    };

    // Table columns for Payment History
    const tableColumns = [
        { title: 'Voucher No', key: 'voucher_number', render: (_, item) => <Text strong>{item.voucher_number || `PV-${item.id}`}</Text> },
        {
            title: 'Supplier',
            key: 'supplier',
            render: (_, item) => {
                const supplier = suppliers.find(s => s.id === parseInt(item.supplier_id));
                return supplier ? supplier.supplier_name : `Supplier #${item.supplier_id}`;
            }
        },
        {
            title: 'Date',
            key: 'date',
            render: (_, item) => item.payment_date ? new Date(item.payment_date).toLocaleDateString('th-TH') : 'Invalid Date',
            sorter: (a, b) => new Date(a.payment_date || 0) - new Date(b.payment_date || 0),
        },
        {
            title: 'Mode',
            key: 'mode',
            render: (_, item) => {
                const mode = item.payment_mode || 'Unknown';
                const color = mode === 'Cash' ? 'green' : mode === 'Bank' ? 'blue' : mode === 'Cheque' ? 'orange' : 'cyan';
                return <Tag color={color}>{mode}</Tag>;
            }
        },
        {
            title: 'Amount',
            key: 'amount',
            render: (_, item) => <Text strong>฿ {(parseFloat(item.amount || 0) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Text>,
            sorter: (a, b) => (parseFloat(a.amount || 0) || 0) - (parseFloat(b.amount || 0) || 0),
        },
        {
            title: 'Discount Amount',
            key: 'discount_amount',
            render: (_, item) => item.discount_amount && parseFloat(item.discount_amount) > 0
                ? <Text style={{ color: '#cf1322' }}>฿{parseFloat(item.discount_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Text>
                : '-'
        },
        { title: 'Ref ID', key: 'ref_id', render: (_, item) => item.reference_id || '-' },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            render: (_, item) => (
                <Tooltip title="Print Payment Voucher">
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<PrinterOutlined />}
                        onClick={() => handlePrintPaymentVoucher(item)}
                    />
                </Tooltip>
            )
        },
    ];

    const selectedModeRequiresBank = paymentMode && paymentModes.find(mode => mode.value === paymentMode)?.requiresBank;

    return (
        <Layout>
            <Header title="Payment Vouchers / ใบสำคัญจ่าย" />
            <ToastContainer />

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '10px',
                        textAlign: 'center'
                    }}>
                        <div className="spinner-border text-warning mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div>Processing...</div>
                    </div>
                </div>
            )}

            {/* Top bar: title + Create Payment Voucher button */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Text type="secondary">Record payments made to suppliers and print vouchers</Text>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setShowCreateModal(true)}
                    >
                        Create Payment Voucher
                    </Button>
                </Col>
            </Row>

            {/* Create Payment Voucher Modal - two fields per row to keep the form compact */}
            <Modal
                title={
                    <Space>
                        <FileTextOutlined style={{ color: '#0d9488' }} />
                        Create Payment Voucher
                    </Space>
                }
                open={showCreateModal}
                onCancel={() => setShowCreateModal(false)}
                footer={null}
                width={680}
            >
                <form onSubmit={handleSubmit} className="payment-form">
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <CalendarOutlined className="me-2" />
                                    Date of Payment <span className="text-danger">*</span>
                                </label>
                                <DatePicker
                                    value={voucherDate}
                                    onChange={(date) => setVoucherDate(date || dayjs())}
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    allowClear={false}
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <UserOutlined className="me-2" />
                                    Supplier Name <span className="text-danger">*</span>
                                </label>
                                <Select
                                    value={selectedSupplier || undefined}
                                    onChange={(value) => getOutStandingBalance(value)}
                                    placeholder="-- Select Supplier --"
                                    style={{ width: '100%' }}
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {suppliers.map((supplier) => (
                                        <Option key={supplier.id} value={supplier.id}>
                                            {supplier.supplier_name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <DollarOutlined className="me-2" />
                                    Outstanding Amount
                                </label>
                                <Input
                                    value={`฿ ${outstandingAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <CreditCardOutlined className="me-2" />
                                    Payment Mode <span className="text-danger">*</span>
                                </label>
                                <Select
                                    value={paymentMode || undefined}
                                    onChange={(value) => {
                                        setPaymentMode(value);
                                        setBankAccountId('');
                                    }}
                                    placeholder="-- Select Payment Mode --"
                                    style={{ width: '100%' }}
                                >
                                    {paymentModes.map(mode => (
                                        <Option key={mode.value} value={mode.value}>
                                            {mode.label}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    </Row>

                    {/* Bank Account Selection - Only show when bank is required */}
                    {selectedModeRequiresBank && (
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="form-group mb-3">
                                    <label className="form-label d-flex align-items-center">
                                        <CreditCardOutlined className="me-2" />
                                        Bank Account <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                        value={bankAccountId || undefined}
                                        onChange={(value) => setBankAccountId(value)}
                                        placeholder="Select Bank Account"
                                        style={{ width: '100%' }}
                                    >
                                        {bankAccounts.filter(acc => acc.is_active).map(account => (
                                            <Option key={account.id} value={account.id}>
                                                {account.account_name} ({account.bank_name}) - {formatCurrency(account.current_balance)}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Discount Received Checkbox */}
                    <Row gutter={16}>
                        <Col xs={24}>
                            <div className="form-group mb-3">
                                <Checkbox
                                    checked={isDiscountGiven}
                                    onChange={(e) => {
                                        setIsDiscountGiven(e.target.checked);
                                        if (!e.target.checked) {
                                            setDiscountAmount('');
                                        }
                                    }}
                                >
                                    Discount Received from Supplier
                                </Checkbox>
                                <br />
                                <small className="text-muted">Check this if the supplier waived off part of the outstanding balance</small>
                            </div>
                        </Col>
                    </Row>

                    {/* Discount Amount - Only show when checkbox is checked */}
                    {isDiscountGiven && (
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="form-group mb-3">
                                    <label className="form-label d-flex align-items-center">
                                        <DollarOutlined className="me-2" />
                                        Discount Amount <span className="text-danger">*</span>
                                    </label>
                                    <InputNumber
                                        min={0}
                                        step={0.01}
                                        value={discountAmount}
                                        onChange={(value) => setDiscountAmount(value)}
                                        style={{ width: '100%' }}
                                        placeholder="0.00"
                                    />
                                    <small className="text-muted">This amount will be deducted from the outstanding balance</small>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Discount Calculation Summary */}
                    {isDiscountGiven && discountAmount && paymentAmount && (
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="mb-3" style={{
                                    background: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
                                    border: '1px solid #feb2b2',
                                    borderRadius: '8px',
                                    padding: '12px 15px'
                                }}>
                                    <strong style={{ color: '#c53030' }}>🏷️ Discount Summary:</strong><br/>
                                    <small style={{ color: '#4a5568' }}>
                                        • Amount Paid: ฿{parseFloat(paymentAmount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br/>
                                        • Discount Received: ฿{parseFloat(discountAmount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br/>
                                        • Total Balance Cleared: ฿{(parseFloat(paymentAmount) + parseFloat(discountAmount)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <DollarOutlined className="me-2" />
                                    Amount Paid <span className="text-danger">*</span>
                                </label>
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    value={paymentAmount}
                                    onChange={(value) => setPaymentAmount(value)}
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label">
                                    Reference Number (Optional)
                                </label>
                                <Input
                                    value={referenceNumber}
                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                    placeholder="Enter reference number"
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24}>
                            <div className="form-group mb-3">
                                <label className="form-label">Remarks (Optional)</label>
                                <Input.TextArea
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    rows={2}
                                    placeholder="Enter any additional notes"
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24}>
                            <div className="form-group mb-4">
                                <label className="form-label d-flex align-items-center">
                                    <DollarOutlined className="me-2" />
                                    Balance After Payment
                                </label>
                                <Input
                                    value={`฿ ${balanceAfterPayment.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                                    readOnly
                                    disabled
                                    style={{ fontWeight: 'bold' }}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        block
                        loading={loading}
                        icon={<FileTextOutlined />}
                    >
                        {loading ? 'Processing...' : 'Save Payment'}
                    </Button>
                </form>
            </Modal>

            {/* Payment History Table */}
            <Card
                title={
                    <Space>
                        <FileTextOutlined style={{ color: '#0d9488' }} />
                        <Text strong>Payment History</Text>
                    </Space>
                }
                extra={
                    <Space>
                        <Button icon={<FileExcelOutlined />} onClick={exportExcel}>Excel</Button>
                        <Button danger icon={<FilePdfOutlined />} onClick={exportPDF}>PDF</Button>
                    </Space>
                }
            >
                {/* Search and Filter Section */}
                <Row gutter={12} className="mb-3">
                    <Col xs={24} md={10} className="mb-2">
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="Search by Voucher No, Supplier ID, Amount, Ref ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} md={6} className="mb-2">
                        <Select
                            value={filterMode}
                            onChange={(value) => setFilterMode(value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="All">All Payment Modes</Option>
                            <Option value="Cash">Cash</Option>
                            <Option value="Bank">Bank</Option>
                            <Option value="Cheque">Cheque</Option>
                            <Option value="Online">Online</Option>
                        </Select>
                    </Col>
                </Row>

                {/* Table */}
                <Table
                    rowKey="id"
                    columns={tableColumns}
                    dataSource={filteredData}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '25', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: true }}
                />

                {/* Summary */}
                <Row gutter={16} className="mt-3 p-3 bg-light-orange border rounded">
                    <Col xs={24} md={8} className="text-center">
                        <Statistic title="Total Records" value={filteredData.length} valueStyle={{ color: '#0d9488' }} />
                    </Col>
                    <Col xs={24} md={8} className="text-center">
                        <Statistic
                            title="Total Amount"
                            value={filteredData.reduce((sum, item) => sum + (parseFloat(item.amount || 0) || 0), 0)}
                            precision={2}
                            prefix="฿"
                            valueStyle={{ color: '#059669' }}
                        />
                    </Col>
                    <Col xs={24} md={8} className="text-center">
                        <Statistic
                            title="Latest Payment"
                            value={filteredData.length > 0 && filteredData[0].payment_date ? new Date(filteredData[0].payment_date).toLocaleDateString('th-TH') : '-'}
                            valueStyle={{ color: '#2563eb', fontSize: 20 }}
                        />
                    </Col>
                </Row>
            </Card>
        </Layout>
    );
}
